/**
 * Stripe Configuration
 *
 * Centralized Stripe configuration with validation.
 * All Stripe keys and price IDs must be provided via environment variables.
 * Missing values will throw errors to ensure configuration issues are caught early.
 */

import { ENV } from './env';

/**
 * Configuration error class for missing Stripe environment variables
 */
export class StripeConfigError extends Error {
  constructor(
    message: string,
    public missingVars: string[],
  ) {
    super(message);
    this.name = 'StripeConfigError';
  }
}

/**
 * Trim and strip a trailing comma from a Stripe price ID env value.
 * Returns empty string if missing or blank after trim.
 * Trailing commas can accidentally occur when copy-pasting from lists or JSON keys.
 */
function sanitizeEnvPriceId(value: string | undefined): string {
  if (!value || value.trim() === '') return '';
  return value.trim().replace(/,$/, '');
}

/**
 * Validates that an environment variable is present
 * @throws {StripeConfigError} if the variable is missing
 */
function requireEnvVar(key: string): string {
  const sanitized = sanitizeEnvPriceId(ENV[key as keyof typeof ENV] as string | undefined);
  if (sanitized === '') {
    throw new StripeConfigError(`Missing required Stripe environment variable: ${key}`, [key]);
  }
  return sanitized;
}

/**
 * Validates multiple environment variables at once
 * @throws {StripeConfigError} if any variables are missing
 */
function requireEnvVars(keys: string[]): Record<string, string> {
  const missing: string[] = [];
  const values: Record<string, string> = {};

  for (const key of keys) {
    const value = ENV[key as keyof typeof ENV];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      missing.push(key);
    } else {
      values[key] = (value as string).trim().replace(/,$/, '');
    }
  }

  if (missing.length > 0) {
    throw new StripeConfigError(
      `Missing required Stripe environment variables: ${missing.join(', ')}`,
      missing,
    );
  }

  return values;
}

// Lazy-loaded configuration - only validated when accessed
// B2B-only: Standard, Pro, Enterprise (no B2C/individual plans, no Ultimate)
let _stripePublishableKey: string | null = null;
let _priceIds: {
  STANDARD_MONTHLY: string;
  STANDARD_ANNUAL: string;
  PRO_MONTHLY: string;
  PRO_ANNUAL: string;
  ENTERPRISE_MONTHLY: string;
  ENTERPRISE_ANNUAL: string;
} | null = null;

let _modelUpgradePriceIds: {
  GEMINI_MONTHLY: string;
  GEMINI_ANNUAL: string;
  ANTHROPIC_MONTHLY: string;
  ANTHROPIC_ANNUAL: string;
  COHERE_MONTHLY: string;
  COHERE_ANNUAL: string;
  GROK_MONTHLY: string;
  GROK_ANNUAL: string;
} | null = null;

let _validPriceIds: Set<string> | null = null;

/**
 * Get Stripe publishable key (lazy-validated)
 * @throws {StripeConfigError} if the key is missing
 */
export function getStripePublishableKey(): string {
  if (!_stripePublishableKey) {
    // Check for VITE_ prefixed version (mapped from STRIPE_PUBLISHABLE_KEY in vite.config.ts)
    _stripePublishableKey = requireEnvVar('VITE_STRIPE_PUBLISHABLE_KEY');
  }
  return _stripePublishableKey;
}

/**
 * Get all price IDs (lazy-validated)
 * @throws {StripeConfigError} if any price IDs are missing
 */
function getPriceIds() {
  if (!_priceIds) {
    const b2bPriceIds = requireEnvVars([
      'VITE_STANDARD_PRICE_ID_MONTHLY',
      'VITE_STANDARD_PRICE_ID_ANNUAL',
      'VITE_PRO_PRICE_ID_MONTHLY',
      'VITE_PRO_PRICE_ID_ANNUAL',
      'VITE_ENTERPRISE_PRICE_ID_MONTHLY',
      'VITE_ENTERPRISE_PRICE_ID_ANNUAL',
    ]);

    _priceIds = {
      // B2B Plans only (Standard, Pro, Enterprise)
      STANDARD_MONTHLY: b2bPriceIds.VITE_STANDARD_PRICE_ID_MONTHLY,
      STANDARD_ANNUAL: b2bPriceIds.VITE_STANDARD_PRICE_ID_ANNUAL,
      PRO_MONTHLY: b2bPriceIds.VITE_PRO_PRICE_ID_MONTHLY,
      PRO_ANNUAL: b2bPriceIds.VITE_PRO_PRICE_ID_ANNUAL,
      ENTERPRISE_MONTHLY: b2bPriceIds.VITE_ENTERPRISE_PRICE_ID_MONTHLY,
      ENTERPRISE_ANNUAL: b2bPriceIds.VITE_ENTERPRISE_PRICE_ID_ANNUAL,
    };
  }
  return _priceIds;
}

/**
 * Exported price IDs for use in pricing plans
 * Accessing properties will trigger lazy validation
 */
export const PRICE_IDS = new Proxy({} as ReturnType<typeof getPriceIds>, {
  get(_target, prop) {
    return getPriceIds()[prop as keyof ReturnType<typeof getPriceIds>];
  },
});

/**
 * Stripe publishable key getter (lazy-validated)
 * Use getStripePublishableKey() function instead of this constant
 * @deprecated Use getStripePublishableKey() function
 */
export const STRIPE_PUBLISHABLE_KEY = getStripePublishableKey();

/**
 * Get model upgrade price IDs (lazy-validated)
 * @throws {StripeConfigError} if any price IDs are missing
 */
function optionalPriceId(key: keyof typeof ENV): string {
  return sanitizeEnvPriceId(ENV[key] as string | undefined);
}

function getModelUpgradePriceIds() {
  if (!_modelUpgradePriceIds) {
    const monthly = requireEnvVars([
      'VITE_MODEL_UPGRADE_GEMINI_MONTHLY',
      'VITE_MODEL_UPGRADE_ANTHROPIC_MONTHLY',
      'VITE_MODEL_UPGRADE_COHERE_MONTHLY',
      'VITE_MODEL_UPGRADE_GROK_MONTHLY',
    ]);

    _modelUpgradePriceIds = {
      GEMINI_MONTHLY: monthly.VITE_MODEL_UPGRADE_GEMINI_MONTHLY,
      GEMINI_ANNUAL: optionalPriceId('VITE_MODEL_UPGRADE_GEMINI_ANNUAL'),
      ANTHROPIC_MONTHLY: monthly.VITE_MODEL_UPGRADE_ANTHROPIC_MONTHLY,
      ANTHROPIC_ANNUAL: optionalPriceId('VITE_MODEL_UPGRADE_ANTHROPIC_ANNUAL'),
      COHERE_MONTHLY: monthly.VITE_MODEL_UPGRADE_COHERE_MONTHLY,
      COHERE_ANNUAL: optionalPriceId('VITE_MODEL_UPGRADE_COHERE_ANNUAL'),
      GROK_MONTHLY: monthly.VITE_MODEL_UPGRADE_GROK_MONTHLY,
      GROK_ANNUAL: optionalPriceId('VITE_MODEL_UPGRADE_GROK_ANNUAL'),
    };
  }
  return _modelUpgradePriceIds;
}

/**
 * Exported model upgrade price IDs for use in model upgrades
 * Accessing properties will trigger lazy validation
 */
export const MODEL_UPGRADE_PRICE_IDS = new Proxy({} as ReturnType<typeof getModelUpgradePriceIds>, {
  get(_target, prop) {
    return getModelUpgradePriceIds()[prop as keyof ReturnType<typeof getModelUpgradePriceIds>];
  },
});

/**
 * Validates if a price ID is a known valid ID (Plan or Model Upgrade)
 * @param priceId - The price ID to validate
 */
export function isValidPriceId(priceId: string | null | undefined): boolean {
  if (!priceId) return false;

  try {
    if (!_validPriceIds) {
      const plans = getPriceIds();
      const modelUpgrades = getModelUpgradePriceIds();
      _validPriceIds = new Set([
        ...Object.values(plans),
        ...Object.values(modelUpgrades).filter((id) => id !== ''),
      ]);
    }
    return _validPriceIds.has(priceId);
  } catch (error) {
    console.error('Failed to validate Stripe price ID:', error);
    // Invalidate cache on error to allow retry
    _validPriceIds = null;
    return false;
  }
}

/**
 * Validates all Stripe configuration
 * This will throw if any required environment variables are missing
 * Call this function to explicitly validate configuration
 */
export function validateStripeConfig(): void {
  getStripePublishableKey();
  getPriceIds();
  getModelUpgradePriceIds();
}
